<template>
    <div class="header-div">
        <form @submit.prevent="handleSubmit" class="stripe-form">
            <div class="title-div">
                <div class="icon-text-header">
                    <div class="icon-div">
                        <svg xmlns="http://www.w3.org/2000/svg" width="380" height="380" viewBox="0 0 480 480" fill="none" class="pattern" opacity="0.4">
                            <defs>
                                <clipPath id="clip-circle">
                                    <rect x="190" y="190" width="240" height="240" />
                                </clipPath>
                            </defs>
                            <g clip-path="url(#clip-circle)">
                                <circle cx="240" cy="240" r="60" stroke="#E4E4E7" />
                                <circle cx="240" cy="240" r="92" stroke="#E4E4E7" />
                                <circle cx="240" cy="240" r="124" stroke="#E4E4E7" />
                                <circle cx="240" cy="240" r="156" stroke="#E4E4E7" />
                            </g>
                        </svg>
                        <CreditCardIcon class="text-gray-500" />
                    </div>
                    <div>
                        <div class="title-section">
                            <p>Update Payment Information</p>
                        </div>
                    </div>
                </div>
                <div class="title-wrapper">                    
                    <div class="info">
                        <div class="name-div">
                            <label>First name</label>
                            <prime-input-text class="w-full" placeholder="John" :class="{ 'Input-invalid': !first_name }" v-model="first_name" @keydown.enter="onEnter" @keydown="letterOnlyInputHandler" />
                            <Message severity="error" v-if="!first_name" class="-mt-2 text-red-600">
                                First name is required.
                            </Message>
                        </div>
                        <div class="name-div">
                            <label>Last name</label>
                            <prime-input-text class="w-full" placeholder="Doe" :class="{ 'Input-invalid': !last_name }" v-model="last_name" @keydown.enter="onEnter" @keydown="letterOnlyInputHandler" />
                            <Message severity="error" v-if="!last_name" class="-mt-2 text-red-600">
                                Last name is required.
                            </Message>
                        </div>
                    </div>
                    <!-- <main-update-payment-info /> -->
                    <div id="card-element"></div>
                </div>
            </div>
            <div class="flex justify-end mt-4 gap-2 pr-6 pb-6">
                <div v-if="hasExistingPaymentMethod" class="flex">
                    <PrimeButton 
                        :label="`Remove ${authStore.user?.identity?.payment_method?.type?.toUpperCase()} ${authStore.user?.identity?.payment_method?.last_four}`"
                        @click="handleRemovePaymentMethod" 
                        class="p-btn-root_error" 
                        :loading="removeLoader"
                        :disabled="paymentLoader"
                    />
                </div>
                <div class="actions">
                    <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="paymentLoader" />
                    <PrimeButton label="Update" type="submit" :loading="paymentLoader" />
                </div>
            </div>
        </form>
    </div>
</template>
<script setup lang="ts">
import { CreditCardIcon } from '@heroicons/vue/24/outline';
import Message from 'primevue/message';
import { billingService } from '~/services/billing-service';
import { useAuthStore } from '~/stores/auth';
import { usePersonStore } from '~/stores/person';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.UPDATE_PAYMENT_INFO);
const stripe = ref<any>();
const elements = ref<any>(null);
const first_name = ref('');
const last_name = ref('');
const paymentLoader = ref(false);
const removeLoader = ref(false);
const personStore = usePersonStore();
const authStore = useAuthStore();
const { showSuccess, showError, showWarning } = useGlobalToast();
const config = useRuntimeConfig();
const { letterOnlyInputHandler } = useUtils();

const closeDialog = () => {
    dialogRef.value.close();
};
onMounted(async () => {
        setupStripe()
    first_name.value = personStore.person?.first_name || '';
    last_name.value = personStore.person?.last_name || '';
});
const setupStripe = async () => {
    const stripeModule = await import('@stripe/stripe-js');
    stripe.value = await stripeModule.loadStripe(config.public.pb.stripeKey);
    const options = {
        mode: "setup",
        currency: "usd",
        paymentMethodCreation: "manual",
        paymentMethodTypes: ["card"],
    };
    elements.value = stripe.value.elements(options);
    if (elements.value) {
        const card = elements.value.create("payment", {
            fields: {
                billingDetails: "auto",
            },
            wallets: { googlePay: "never", applePay: "never", link: "never" },
            terms: { card: "never" },
        });
        card.mount('#card-element');
    }
};
const handleSubmit = async () => {
    if (!first_name.value || !last_name.value) {
        return;
    }
    paymentLoader.value = true;
    if (!elements.value) return;
    await elements.value.submit();

    try {
        const result = await stripe.value.createPaymentMethod({
            elements: elements.value,
            params: {
                billing_details: {
                    name: first_name.value + ' ' + last_name.value,
                }
            },
        });

        paymentLoader.value = false;
        
        if (result.error) {
            showError('Error', result.error.message || 'Could not add payment method.');
            return;
        }

        if (result.paymentMethod?.id) {
            const data = {
                payment_method: result.paymentMethod.id,
                first_name: first_name.value,
                last_name: last_name.value,
            };
            
            await billingService.updatePaymentMethod(data);
            await authStore.getMe(); // Refresh auth data
            
            personStore.getPerson();
            showSuccess('Success', 'Payment method successfully updated');
            closeDialog();
        }
    } catch (error) {
        paymentLoader.value = false;
        showError('Error', 'Could not add payment method.');
    }
};

const handleRemovePaymentMethod = async () => {
    removeLoader.value = true;
    try {
        await billingService.removePaymentMethod();
        await authStore.getMe(); // Refresh auth data
        await personStore.getPerson();
        showSuccess('Success', 'Payment method successfully removed');
        closeDialog();
    } catch (error: any) {
        showError('Error', error.message || 'Could not remove payment method.');
    } finally {
        removeLoader.value = false;
    }
};

const hasExistingPaymentMethod = computed(() => {
    return authStore.user?.identity?.payment_method?.type
});

const onEnter = (e: KeyboardEvent) => {
    if (first_name.value && last_name.value && !paymentLoader.value) {
        handleSubmit();
    }
};
</script>
<style lang="scss" scoped>
.stripe-form {
    @apply w-full;
}
.header-div {
    @apply sm:max-w-xl sm:w-full w-11/12 rounded-lg bg-white mx-auto h-auto overflow-y-auto;
}
.title-div {
    @apply flex flex-col p-6 pb-0 items-start gap-1 self-stretch w-full;
}
.icon-text-header{
    @apply flex flex-row items-center gap-4 items-start w-full ;
}
.icon-div {
    @apply relative flex w-12 h-12 p-3 justify-center items-center rounded-full bg-gray-100;
}
.title-wrapper {
    @apply flex flex-col items-start gap-1 sm:flex-1 sm:w-auto w-full sm:pl-16;
}
.title-section {
    @apply self-stretch flex flex-row justify-between relative;

    p {
        @apply text-zinc-950 font-inter text-lg font-semibold leading-7;
    }
}
.close-icon {
    @apply flex justify-center items-center rounded-lg text-gray-400 cursor-pointer;
}
.sub-title {
    @apply text-zinc-600 font-inter text-sm font-normal leading-5;
}
.pattern {
    @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none;
}
.payment-modal {
    @apply bg-white p-8 rounded-lg max-w-md;
}
#card-element {
    @apply w-full lg:max-w-lg;
}
.name-div {
    @apply flex flex-col mb-3 sm:w-1/2 w-full;

    label {
        @apply text-zinc-800 text-sm mb-1;
    }
}
.main-footer {
    @apply flex flex-col justify-center items-end pt-8;
}
.divider {
    @apply h-px self-stretch bg-zinc-300 my-5;
}
.actions-div {
    @apply flex justify-end items-center gap-3 px-6 pb-6;
}
.Input-invalid {
    @apply text-red-600 border-2 border-red-600 shadow-sm;
}
.info {
    @apply flex flex-col lg:flex-row gap-0 lg:gap-2 w-full;
}
.actions {
    @apply flex gap-2;
}
.stripe-form{
    @apply w-full;
}
</style>
